/**
 * MUTAGIM.SHOP — Phase 17: Admin Permissions
 * Gestion des rôles et permissions admin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield, Users, Plus, ChevronLeft, Loader2, Mail, Trash2,
  Check, X, Settings, UserPlus, Key
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des modules disponibles
const AVAILABLE_MODULES = [
  { id: 'orders', name: 'Commandes', description: 'Gérer les commandes' },
  { id: 'products', name: 'Produits', description: 'Gérer le catalogue' },
  { id: 'customers', name: 'Clients', description: 'Gérer les clients' },
  { id: 'analytics', name: 'Analytics', description: 'Voir les statistiques' },
  { id: 'promotions', name: 'Promotions', description: 'Gérer les promos et flash sales' },
  { id: 'social', name: 'Social Media', description: 'Studio de création' },
  { id: 'crew', name: 'Mutagim Crew', description: 'Programme d\'affiliation' },
  { id: 'reviews', name: 'Avis', description: 'Modérer les avis' },
  { id: 'agents', name: 'Agents', description: 'Gérer les fournisseurs' },
  { id: 'stocks', name: 'Stocks', description: 'Gérer les inventaires' },
  { id: 'sub_orders', name: 'Sous-commandes', description: 'Gérer les sous-commandes' },
  { id: 'notifications', name: 'Notifications', description: 'Centre de notifications' },
  { id: 'settings', name: 'Paramètres', description: 'Configuration système' },
];

function getToken() {
  return localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
}

export function AdminPermissionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('admins');
  
  // Dialog states
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  
  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Report config
  const [reportConfig, setReportConfig] = useState({
    weekly_report_enabled: false,
    report_email: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      // Fetch roles
      const rolesRes = await fetch(`${API_URL}/api/analytics/permissions/roles`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.roles || []);
      }

      // Fetch admins
      const adminsRes = await fetch(`${API_URL}/api/analytics/permissions/admins`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (adminsRes.ok) {
        const data = await adminsRes.json();
        setAdmins(data.admins || []);
      }

      // Fetch report config
      const configRes = await fetch(`${API_URL}/api/analytics/reports/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (configRes.ok) {
        const data = await configRes.json();
        setReportConfig(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const inviteAdmin = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteRole) {
      toast.error('Remplissez tous les champs');
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/permissions/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail, role_id: inviteRole }),
      });

      if (res.ok) {
        toast.success('Invitation envoyée (mode LOG)');
        setShowInviteDialog(false);
        setInviteEmail('');
        setInviteRole('');
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const createRole = async (e) => {
    e.preventDefault();
    if (!newRoleName || newRolePermissions.length === 0) {
      toast.error('Nommez le rôle et sélectionnez des permissions');
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/permissions/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newRoleName, permissions: newRolePermissions }),
      });

      if (res.ok) {
        toast.success('Rôle créé !');
        setShowRoleDialog(false);
        setNewRoleName('');
        setNewRolePermissions([]);
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const revokeAdmin = async (adminId) => {
    if (!confirm('Révoquer les droits admin de cet utilisateur ?')) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/permissions/admins/${adminId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Droits admin révoqués');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteRole = async (roleId) => {
    if (!confirm('Supprimer ce rôle personnalisé ?')) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/permissions/roles/${roleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Rôle supprimé');
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateReportConfig = async (enabled, email = null) => {
    try {
      const token = getToken();
      const url = new URL(`${API_URL}/api/analytics/reports/config`);
      url.searchParams.append('enabled', enabled);
      if (email) url.searchParams.append('email', email);

      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Configuration mise à jour');
        setReportConfig(prev => ({ ...prev, weekly_report_enabled: enabled, report_email: email || prev.report_email }));
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const togglePermission = (moduleId) => {
    setNewRolePermissions(prev => 
      prev.includes(moduleId) 
        ? prev.filter(p => p !== moduleId)
        : [...prev, moduleId]
    );
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.name || roleId;
  };

  return (
    <AdminPageWrapper title="Permissions & Rapports" subtitle="Gérez les accès et les rapports">
      <div data-testid="admin-permissions-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
              <TabsTrigger value="admins" className="data-[state=active]:bg-zinc-800">
                <Users className="w-4 h-4 mr-2" />
                Administrateurs
              </TabsTrigger>
              <TabsTrigger value="roles" className="data-[state=active]:bg-zinc-800">
                <Shield className="w-4 h-4 mr-2" />
                Rôles
              </TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-zinc-800">
                <Mail className="w-4 h-4 mr-2" />
                Rapports
              </TabsTrigger>
            </TabsList>

            {/* Admins Tab */}
            <TabsContent value="admins">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-white">Administrateurs ({admins.length})</h2>
                <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="invite-admin">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Inviter un admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle className="text-white">Inviter un administrateur</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={inviteAdmin} className="space-y-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="nouvel.admin@example.com"
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label>Rôle</Label>
                        <Select value={inviteRole} onValueChange={setInviteRole}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" disabled={saving} className="w-full">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                        Envoyer l'invitation
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                </div>
              ) : (
                <div className="grid gap-4">
                  {admins.map((admin) => (
                    <Card key={admin.id} className="bg-zinc-900/50 border-zinc-800">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                            <Users className="w-6 h-6 text-orange-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">
                              {admin.first_name} {admin.last_name}
                            </p>
                            <p className="text-sm text-white">{admin.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-purple-500/20 text-purple-400">
                            {getRoleName(admin.role_id)}
                          </Badge>
                          {admin.role_id !== 'super_admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => revokeAdmin(admin.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Roles Tab */}
            <TabsContent value="roles">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-white">Rôles ({roles.length})</h2>
                <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-role">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un rôle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-white">Créer un rôle personnalisé</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={createRole} className="space-y-4">
                      <div>
                        <Label>Nom du rôle</Label>
                        <Input
                          value={newRoleName}
                          onChange={(e) => setNewRoleName(e.target.value)}
                          placeholder="Ex: Gestionnaire de stock"
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label className="mb-3 block">Permissions</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {AVAILABLE_MODULES.map((module) => (
                            <div 
                              key={module.id}
                              onClick={() => togglePermission(module.id)}
                              className={cn(
                                "p-3 rounded-lg border cursor-pointer transition-all",
                                newRolePermissions.includes(module.id)
                                  ? "border-orange-500 bg-orange-500/10"
                                  : "border-zinc-700 hover:border-zinc-600"
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <Checkbox 
                                  checked={newRolePermissions.includes(module.id)}
                                  className="pointer-events-none"
                                />
                                <span className="text-white text-sm">{module.name}</span>
                              </div>
                              <p className="text-xs text-gray-200 mt-1">{module.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button type="submit" disabled={saving} className="w-full">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Créer le rôle
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid gap-4">
                {roles.map((role) => (
                  <Card key={role.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            role.predefined ? "bg-purple-500/20" : "bg-orange-500/20"
                          )}>
                            <Shield className={cn(
                              "w-5 h-5",
                              role.predefined ? "text-purple-400" : "text-orange-400"
                            )} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{role.name}</p>
                            <Badge className={role.predefined ? "bg-purple-500/20 text-purple-400" : "bg-orange-500/20 text-orange-400"}>
                              {role.predefined ? 'Prédéfini' : 'Personnalisé'}
                            </Badge>
                          </div>
                        </div>
                        {!role.predefined && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteRole(role.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {role.permissions.includes('*') ? (
                          <Badge className="bg-green-500/20 text-green-400">Accès complet</Badge>
                        ) : (
                          role.permissions.map((perm) => {
                            const module = AVAILABLE_MODULES.find(m => m.id === perm);
                            return (
                              <Badge key={perm} variant="secondary" className="text-xs">
                                {module?.name || perm}
                              </Badge>
                            );
                          })
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    Rapport hebdomadaire automatique
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">Activer le rapport hebdomadaire</p>
                      <p className="text-sm text-white">
                        Envoyé chaque lundi à 08h00
                      </p>
                    </div>
                    <Switch
                      checked={reportConfig.weekly_report_enabled}
                      onCheckedChange={(checked) => updateReportConfig(checked)}
                      data-testid="weekly-report-toggle"
                    />
                  </div>

                  {reportConfig.weekly_report_enabled && (
                    <div>
                      <Label>Email destinataire</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          type="email"
                          value={reportConfig.report_email || ''}
                          onChange={(e) => setReportConfig(prev => ({ ...prev, report_email: e.target.value }))}
                          placeholder="rapports@mutagim.shop"
                          className="bg-zinc-800 border-zinc-700 flex-1"
                        />
                        <Button 
                          onClick={() => updateReportConfig(true, reportConfig.report_email)}
                        >
                          Sauvegarder
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="bg-zinc-800/30 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-3">Contenu du rapport :</h4>
                    <ul className="space-y-2 text-sm text-white">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        CA semaine vs semaine précédente
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        Nombre de commandes + panier moyen
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        Top 3 produits vendus
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        Commissions Crew dues
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        Soldes agents actuels
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        Alertes actives (stocks, erreurs)
                      </li>
                    </ul>
                  </div>

                  <p className="text-xs text-gray-200">
                    Mode LOG actif — Les emails sont simulés et enregistrés dans les logs
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
    </AdminPageWrapper>
  );
}

export default AdminPermissionsPage;
